/**
 * ECharts Adapter
 *
 * Converts generic chart types to ECharts-specific configuration.
 * This is the only layer that needs to change if we swap charting libraries.
 */

import * as echarts from 'echarts';
import { EChartsOption } from 'echarts';
import { BarChartProps, ChartConfig, ChartSeries, LineChartProps, LoadingChartProps, PieChartProps } from '../types';

// Smallest gap allowed between time-axis ticks: one month. Without it ECharts
// subdivides short spans down to weeks and the labels smear together.
const MIN_TIME_TICK_INTERVAL_MS = 31 * 24 * 60 * 60 * 1000;

// Target tick count that stays readable in a narrow chart; ECharts widens the
// step to quarters or years on its own as the span grows.
const TIME_AXIS_SPLIT_NUMBER = 4;

/**
 * Get sorted unique x-axis values from all series (shared by line and bar adapters).
 */
function getXAxisDataFromSeries(series: ChartSeries[]): string[] {
  return Array.from(new Set(series.flatMap(s => s.data).map(point => String(point.x)))).sort((a, b) => {
    const numA = Number(a);
    const numB = Number(b);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    return a.localeCompare(b);
  });
}

/**
 * Map series data to ECharts y-values in xAxisData order.
 */
function mapSeriesToData(series: ChartSeries, xAxisData: string[]): (number | null)[] {
  return xAxisData.map(xValue => {
    const point = series.data.find(p => String(p.x) === xValue);
    return point?.y ?? null;
  });
}

/**
 * Convert generic chart config to ECharts format
 */
const convertChartConfig = (config?: ChartConfig): Partial<EChartsOption> => {
  if (!config) {
    return {};
  }

  const echartsConfig: Partial<EChartsOption> = {};

  // Title
  if (config.title) {
    echartsConfig.title = {
      text: config.title,
      subtext: config.subtitle,
      left: 'center',
    };
  }

  // Tooltip with glassmorphism styling
  echartsConfig.tooltip = {
    show: config.tooltip?.show !== false,
    trigger: 'axis',
    appendToBody: true,
    axisPointer: {
      type: 'cross',
      crossStyle: {
        color: '#1A64B1',
        opacity: 0.3,
      },
      lineStyle: {
        color: '#52A3FF',
        type: 'dashed',
        opacity: 0.5,
      },
    },
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderColor: 'rgba(82, 163, 255, 0.3)',
    borderWidth: 1,
    borderRadius: 12,
    padding: [12, 16],
    textStyle: {
      color: '#082444',
      fontSize: 13,
    },
    extraCssText: 'backdrop-filter: blur(12px); box-shadow: 0 8px 32px rgba(26, 100, 177, 0.15);',
  };

  // Legend
  if (config.legend?.show !== false) {
    const position = config.legend?.position;
    let legendLeft = 'center';
    if (position === 'left') {
      legendLeft = 'left';
    } else if (position === 'right') {
      legendLeft = 'right';
    }
    let legendTop = 'auto';
    if (position === 'top') {
      legendTop = 'top';
    } else if (position === 'bottom') {
      legendTop = 'bottom';
    }
    echartsConfig.legend = {
      show: true,
      orient: position === 'left' || position === 'right' ? 'vertical' : 'horizontal',
      left: legendLeft,
      top: legendTop,
    };
  } else {
    echartsConfig.legend = { show: false };
  }

  // Grid
  echartsConfig.grid = {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true,
  };

  return echartsConfig;
};

/**
 * Convert generic line chart props to ECharts option
 */
export const convertLineChartToECharts = (props: LineChartProps): EChartsOption => {
  const { series, config, area = false, showPoints = true, smooth = false } = props;

  const isTimeAxis = config?.xAxis?.type === 'time';
  const xAxisData = isTimeAxis ? [] : getXAxisDataFromSeries(series);

  const echartsSeries = series
    .filter(s => s.visible !== false)
    .map(s => {
      // A time axis places each point at its true time coordinate via [x, y]
      // pairs; a category axis keeps positionally mapped y values.
      const data = isTimeAxis ? s.data.map(point => [point.x, point.y]) : mapSeriesToData(s, xAxisData);
      const seriesColor = s.color || '#52A3FF';

      return {
        name: s.label,
        type: 'line',
        data,
        smooth,
        areaStyle: area
          ? {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: hexToRgba(seriesColor, 0.5) },
              { offset: 0.7, color: hexToRgba(seriesColor, 0.12) },
              { offset: 1, color: hexToRgba(seriesColor, 0.02) },
            ]),
          }
          : undefined,
        lineStyle: {
          width: 3,
          color: seriesColor,
          shadowColor: hexToRgba(seriesColor, 0.37),
          shadowBlur: 8,
          shadowOffsetY: 4,
        },
        symbol: showPoints ? 'circle' : 'none',
        symbolSize: showPoints ? 6 : 0,
        itemStyle: {
          color: seriesColor,
          borderWidth: 2,
          borderColor: '#fff',
        },
        emphasis: {
          scale: true,
          symbolSize: 12,
          itemStyle: {
            borderWidth: 3,
            borderColor: '#fff',
            shadowBlur: 12,
            shadowColor: hexToRgba(seriesColor, 0.5),
          },
        },
        ...s.config,
      };
    });

  // X-axis configuration. A 'time' axis spaces points by true elapsed time and
  // takes no category data (ECharts reads coordinates from the [x, y] pairs);
  // boundaryGap is a category-only setting.
  const xAxis: EChartsOption['xAxis'] = {
    ...(isTimeAxis
      ? ({
        type: 'time',
        minInterval: MIN_TIME_TICK_INTERVAL_MS,
        splitNumber: TIME_AXIS_SPLIT_NUMBER,
      } as const)
      : ({ type: 'category', data: xAxisData, boundaryGap: false } as const)),
    axisLine: { lineStyle: { color: '#E0E0E0' } },
    axisLabel: {
      color: '#9E9E9E',
      fontSize: 11,
      ...(isTimeAxis && { hideOverlap: true }),
      ...(config?.xAxis?.formatter && { formatter: config.xAxis.formatter }),
    },
    splitLine: { show: false },
    ...(config?.xAxis?.label && { name: config.xAxis.label }),
  };

  // Y-axis configuration
  const yAxis: EChartsOption['yAxis'] = {
    type: 'value',
    axisLine: { show: false },
    axisLabel: {
      color: '#9E9E9E',
      fontSize: 11,
      ...(config?.yAxis?.formatter && { formatter: config.yAxis.formatter }),
    },
    splitLine: { lineStyle: { color: '#F5F5F5', type: 'dashed' } },
    ...(config?.yAxis?.label && { name: config.yAxis.label }),
    ...(config?.yAxis?.min !== undefined && { min: config.yAxis.min }),
    ...(config?.yAxis?.max !== undefined && { max: config.yAxis.max }),
  };

  // Base config
  const baseConfig = convertChartConfig(config);

  return {
    ...baseConfig,
    xAxis,
    yAxis,
    series: echartsSeries,
    animationDuration: 1500,
    animationEasing: 'cubicOut',
    animationDelay: (idx: number) => idx * 100,
    ...config?.options,
  } as EChartsOption;
};

/**
 * Convert generic bar chart props to ECharts option
 */
export const convertBarChartToECharts = (props: BarChartProps): EChartsOption => {
  const { series, config, stacked = false, horizontal = false, barWidth, barCategoryGap, barGap } = props;

  const xAxisData = getXAxisDataFromSeries(series);

  const echartsSeries = series
    .filter(s => s.visible !== false)
    .map(s => {
      const data = mapSeriesToData(s, xAxisData);

      return {
        name: s.label,
        type: 'bar',
        data,
        stack: stacked ? 'stack' : undefined,
        itemStyle: {
          color: s.color,
        },
        ...(barWidth !== undefined && { barWidth }),
        ...(barCategoryGap !== undefined && { barCategoryGap }),
        ...(barGap !== undefined && { barGap }),
        ...s.config,
      };
    });

  // X-axis configuration
  const xAxis: EChartsOption['xAxis'] = horizontal
    ? {
      type: 'value',
      ...(config?.xAxis?.label && { name: config.xAxis.label }),
      ...(config?.xAxis?.min !== undefined && { min: config.xAxis.min }),
      ...(config?.xAxis?.max !== undefined && { max: config.xAxis.max }),
      ...(config?.xAxis?.formatter && {
        axisLabel: {
          formatter: config.xAxis.formatter,
        },
      }),
    }
    : {
      type: 'category',
      data: xAxisData,
      ...(config?.xAxis?.label && { name: config.xAxis.label }),
      ...(config?.xAxis?.formatter && {
        axisLabel: {
          formatter: config.xAxis.formatter,
        },
      }),
    };

  // Y-axis configuration
  const yAxis: EChartsOption['yAxis'] = horizontal
    ? {
      type: 'category',
      data: xAxisData,
      ...(config?.yAxis?.label && { name: config.yAxis.label }),
      ...(config?.yAxis?.formatter && {
        axisLabel: {
          formatter: config.yAxis.formatter,
        },
      }),
    }
    : {
      type: 'value',
      ...(config?.yAxis?.label && { name: config.yAxis.label }),
      ...(config?.yAxis?.min !== undefined && { min: config.yAxis.min }),
      ...(config?.yAxis?.max !== undefined && { max: config.yAxis.max }),
      ...(config?.yAxis?.formatter && {
        axisLabel: {
          formatter: config.yAxis.formatter,
        },
      }),
    };

  // Base config
  const baseConfig = convertChartConfig(config);

  return {
    ...baseConfig,
    xAxis,
    yAxis,
    series: echartsSeries,
    ...config?.options,
  } as EChartsOption;
};

/**
 * Convert generic pie chart props to ECharts option
 */
export const convertPieChartToECharts = (props: PieChartProps): EChartsOption => {
  const { data, config, showLabels = true, showPercentages = true, innerRadius = 0 } = props;

  // Convert data points to ECharts format
  const echartsData = data.map(point => ({
    name: String(point.x),
    value: point.y,
  }));

  // Series configuration
  const series: EChartsOption['series'] = [
    {
      name: props.label || 'Data',
      type: 'pie',
      radius: innerRadius > 0 ? [`${innerRadius * 100}%`, '70%'] : '70%',
      data: echartsData,
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)',
        },
      },
      label: {
        show: showLabels,
        formatter: showPercentages ? '{b}: {c} ({d}%)' : '{b}: {c}',
      },
      labelLine: {
        show: showLabels,
      },
    },
  ];

  // Base config
  const baseConfig = convertChartConfig(config);

  // For pie charts, tooltip should be item-based, not axis-based
  const tooltip: EChartsOption['tooltip'] = {
    show: config?.tooltip?.show !== false,
    trigger: 'item',
    formatter: config?.tooltip?.formatter
      ? (params: any) => {
        const dataPoint = data.find(p => String(p.x) === params.name);
        if (dataPoint && config?.tooltip?.formatter) {
          return config.tooltip.formatter(dataPoint, { id: '', label: params.name, data: [dataPoint] });
        }
        return `${params.name}: ${params.value} (${params.percent}%)`;
      }
      : '{b}: {c} ({d}%)',
  };

  return {
    ...baseConfig,
    tooltip,
    series,
    ...config?.options,
  } as EChartsOption;
};

/**
 * Convert generic loading/liquid fill chart props to ECharts option
 *
 * Note: This requires the echarts-liquidfill extension to be installed and imported.
 * Install with: npm install echarts-liquidfill
 * Import with: import 'echarts-liquidfill'
 */
export const convertLoadingChartToECharts = (props: LoadingChartProps): EChartsOption => {
  const {
    value,
    label,
    showLabel = true,
    color = '#0A6CDE',
    waveColors,
    backgroundColor = '#E6F2FF',
    showOutline = true,
    outlineBorderWidth = 2,
    outlineBorderColor = '#0A6CDE',
    waveAnimationSpeed = 1,
    waveAmplitude = 8,
    shape = 'circle',
    config,
  } = props;

  // Clamp value between 0 and 100
  const normalizedValue = Math.max(0, Math.min(100, value)) / 100;

  // Determine wave colors - use provided colors or generate from base color
  const colors = waveColors || [
    color, // Main wave color
    adjustColorBrightness(color, -0.15), // Darker wave for depth
    adjustColorBrightness(color, 0.1), // Lighter wave
  ];

  // Create wave data - multiple waves for layered effect
  // Each wave is slightly offset to create depth
  const waveData = colors.map((waveColor, index) => {
    // Slightly offset each wave for layered effect
    const waveValue = normalizedValue - index * 0.02;
    return Math.max(0, waveValue);
  });

  // For multiple waves with different colors, use object format
  // Otherwise use simple number array
  const liquidFillData =
    colors.length > 1
      ? waveData.map((waveValue, index) => {
        const opacityMap: Record<number, number> = { 0: 0.8, 1: 0.6 };
        const opacity = opacityMap[index] ?? 0.4;
        return {
          value: waveValue,
          itemStyle: {
            color: colors[index],
            opacity,
          },
        };
      })
      : waveData;

  // Series configuration for liquid fill
  const series: any = [
    {
      type: 'liquidFill',
      data: liquidFillData,
      shape,
      color: colors.length === 1 ? colors[0] : colors, // Single color or array for multiple waves
      itemStyle: {
        opacity: 0.8,
        shadowBlur: 0,
      },
      outline: {
        show: showOutline,
        borderDistance: 0,
        itemStyle: {
          borderColor: outlineBorderColor,
          borderWidth: outlineBorderWidth,
          shadowBlur: 0,
        },
      },
      backgroundStyle: {
        color: backgroundColor,
      },
      label: {
        show: showLabel,
        color: '#FFFFFF',
        fontSize: 50,
        fontWeight: 'bold',
        formatter: label !== undefined ? label : `${Math.round(value)}%`,
      },
      waveAnimation: true,
      animationDuration: 2000 / waveAnimationSpeed,
      animationDurationUpdate: 2000 / waveAnimationSpeed,
      amplitude: waveAmplitude,
      emphasis: {
        itemStyle: {
          opacity: 0.95,
        },
      },
    },
  ];

  // Base config
  const baseConfig = convertChartConfig(config);

  // For liquid fill charts, disable tooltip by default (or customize)
  const tooltip: EChartsOption['tooltip'] = {
    show: config?.tooltip?.show === true,
    trigger: 'item',
    formatter: () => `${Math.round(value)}%`,
  };

  return {
    ...baseConfig,
    tooltip,
    series,
    ...config?.options,
  } as EChartsOption;
};

/**
 * Convert a hex color to rgba string
 */
function hexToRgba(color: string, alpha: number): string {
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = Math.floor(num / 65536);
  const g = Math.floor(num / 256) % 256;
  const b = num % 256;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Helper function to adjust color brightness
 */
function adjustColorBrightness(color: string, percent: number): string {
  // Remove # if present
  const hex = color.replace('#', '');

  // Convert to RGB
  const num = parseInt(hex, 16);
  const r = Math.floor(num / 65536) + Math.round(255 * percent);
  const g = (Math.floor(num / 256) % 256) + Math.round(255 * percent);
  const b = (num % 256) + Math.round(255 * percent);

  // Clamp values
  const clampedR = Math.max(0, Math.min(255, r));
  const clampedG = Math.max(0, Math.min(255, g));
  const clampedB = Math.max(0, Math.min(255, b));

  // Convert back to hex
  const hexValue = (16777216 + clampedR * 65536 + clampedG * 256 + clampedB).toString(16).slice(1);
  return `#${hexValue}`;
}
